import { fetchJSON as fetchAPI } from './client';

export type BIOpsDashboard = {
  tenant_id: string;
  total_sales: number;
  sales_growth_pct: number;
  total_sessions: number;
  avg_conversion_rate: number;
  avg_buy_box_pct: number;
  brand_share_avg: number;
  repeat_purchase_rate: number;
  overall_business_score: number;
  active_insights: number;
  active_recommendations: number;
  generated_at?: string;
};

export type BusinessMetricRecord = {
  id: string;
  period?: string;
  sales: number;
  units_ordered: number;
  sessions: number;
  page_views: number;
  buy_box_pct: number;
  conversion_rate: number;
  avg_order_value: number;
  organic_sales: number;
  sponsored_sales: number;
  sales_trend?: string;
};

export type BrandAnalyticsRecord = {
  id: string;
  search_term?: string;
  search_frequency_rank: number;
  click_share: number;
  conversion_share: number;
  brand_share: number;
  category_share: number;
  top_clicked_asin?: string;
  top_purchased_asin?: string;
};

export type SearchMetricRecord = {
  id: string;
  keyword: string;
  current_rank: number;
  previous_rank: number;
  rank_change: number;
  search_demand: number;
  click_share: number;
  conversion_share: number;
  organic_share: number;
  sponsored_influence: number;
  trend?: string;
};

export type CustomerMetricRecord = {
  id: string;
  cohort_name?: string;
  repeat_purchase_rate: number;
  retention_rate: number;
  customer_value: number;
  market_basket_size: number;
  cross_sell_opportunity?: string;
};

export type ExecutiveInsightRecord = {
  id: string;
  type: string;
  title: string;
  summary?: string;
  evidence?: string;
  impact_pct?: number;
  confidence_score: number;
  severity?: string;
};

export type ForecastRecord = {
  id: string;
  metric?: string;
  status: string;
  forecast_revenue: number;
  forecast_orders: number;
  forecast_traffic: number;
  forecast_conversion: number;
  forecast_profit: number;
  horizon_days: number;
};

export type ExecutiveScorecard = {
  id: string;
  revenue_score: number;
  growth_score: number;
  profit_score: number;
  traffic_score: number;
  brand_score: number;
  customer_score: number;
  operational_score: number;
  overall_business_score: number;
};

export type BIRecommendation = {
  id: string;
  type: string;
  title: string;
  description?: string;
  sku?: string;
  category?: string;
  expected_impact?: string;
  confidence_score: number;
  priority_rank: number;
  requires_approval: boolean;
  source_module?: string;
};

const API_BASE = import.meta.env.VITE_BIOPS_API ?? '/api/v1/biops';

function fetchJSON<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(API_BASE, path, init);
}

export const biopsApi = {
  dashboard: () => fetchJSON<BIOpsDashboard>('/dashboard'),
  listBusiness: () => fetchJSON<BusinessMetricRecord[]>('/business'),
  listBrandAnalytics: () => fetchJSON<BrandAnalyticsRecord[]>('/brand-analytics'),
  listSearch: () => fetchJSON<SearchMetricRecord[]>('/search'),
  listCustomer: () => fetchJSON<CustomerMetricRecord[]>('/customer'),
  listInsights: () => fetchJSON<ExecutiveInsightRecord[]>('/insights'),
  listForecasts: () => fetchJSON<ForecastRecord[]>('/forecasts'),
  getScorecard: () => fetchJSON<ExecutiveScorecard>('/scorecard'),
  listRecommendations: () => fetchJSON<BIRecommendation[]>('/recommendations'),
  generateRecommendations: () =>
    fetchJSON<BIRecommendation[]>('/recommendations/generate', { method: 'POST' }),
  listSyncJobs: () => fetchJSON<{ id: string; mode: string; status: string }[]>('/sync-jobs'),
  listActivity: () => fetchJSON<{ id: string; message: string }[]>('/activity'),
};
